import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { History, User, Users, Calendar, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const actionColors = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  acknowledge: 'bg-purple-100 text-purple-800'
};

const resourceIcons = {
  user: User,
  team: Users,
  appointment: Calendar,
  team_member: Users,
  notification: History
};

export default function AuditLogDialog({ open, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    resource_type: '',
    action: ''
  });

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.resource_type) params.append('resource_type', filters.resource_type);
      if (filters.action) params.append('action', filters.action);
      params.append('limit', '100');

      const response = await axios.get(`${API_URL}/api/audit-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchLogs();
    }
  }, [open, filters]);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDetails = (details) => {
    if (!details) return null;
    
    if (details.changes) {
      return Object.entries(details.changes).map(([key, value]) => (
        <span key={key} className="text-xs">
          {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
        </span>
      ));
    }
    
    if (details.before && details.after) {
      return (
        <span className="text-xs">
          {details.before.name} → {details.after.name}
        </span>
      );
    }

    return Object.entries(details).map(([key, value]) => (
      <span key={key} className="text-xs mr-2">
        {key}: {String(value)}
      </span>
    ));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh]" data-testid="audit-log-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0f172a] flex items-center gap-2">
            <History className="w-6 h-6" />
            Audit Logs
          </DialogTitle>
        </DialogHeader>

        {/* Filters */}
        <div className="flex gap-4 py-2">
          <div className="flex-1">
            <Select
              value={filters.resource_type || "all"}
              onValueChange={(value) => setFilters({ ...filters, resource_type: value === "all" ? "" : value })}
            >
              <SelectTrigger data-testid="filter-resource-type">
                <SelectValue placeholder="All Resources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                <SelectItem value="user">Users</SelectItem>
                <SelectItem value="team">Teams</SelectItem>
                <SelectItem value="appointment">Appointments</SelectItem>
                <SelectItem value="team_member">Team Members</SelectItem>
                <SelectItem value="notification">Notifications</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Select
              value={filters.action || "all"}
              onValueChange={(value) => setFilters({ ...filters, action: value === "all" ? "" : value })}
            >
              <SelectTrigger data-testid="filter-action">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create">Created</SelectItem>
                <SelectItem value="update">Updated</SelectItem>
                <SelectItem value="delete">Deleted</SelectItem>
                <SelectItem value="acknowledge">Acknowledged</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={fetchLogs}
            disabled={loading}
            data-testid="refresh-logs"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <div className="text-sm text-slate-500 mb-2">
          Showing {logs.length} of {total} logs
        </div>

        <ScrollArea className="h-[400px] pr-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-slate-500">
              <History className="w-8 h-8 mb-2" />
              <p>No audit logs found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => {
                const IconComponent = resourceIcons[log.resource_type] || History;
                return (
                  <div
                    key={log.id}
                    className="p-3 bg-slate-50 rounded-lg border border-slate-200"
                    data-testid={`audit-log-${log.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-white rounded-md border border-slate-200">
                          <IconComponent className="w-4 h-4 text-slate-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${actionColors[log.action]}`}>
                              {log.action}
                            </span>
                            <span className="text-sm font-medium text-slate-700 capitalize">
                              {log.resource_type.replace('_', ' ')}
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-[#0f172a]">
                            {log.resource_name}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            by {log.user_name} ({log.user_email})
                          </p>
                          {log.details && (
                            <div className="mt-2 text-slate-600 flex flex-wrap gap-2">
                              {formatDetails(log.details)}
                            </div>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-slate-500 whitespace-nowrap">
                        {formatDate(log.timestamp)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <div className="flex justify-end pt-4">
          <Button onClick={onClose} data-testid="close-audit-log">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
